import React, { Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import getWafToken from '@/api/wafToken';
import CookieBanner from './components/cookieBanner/CookieBanner';
import { useScrollToHash } from './hooks/useScrollToHash';
import Compare from './pages/compare/Compare';
import ContactUs from './pages/contactUs/ContactUs';
import Home from './pages/home/Home';
import DataDeletion from './pages/legal/DataDeletion';
import AppCallback from './pages/appCallback/AppCallback';
import AppGet from './pages/appGet/AppGet';
import AppRedirect from './pages/appRedirect/AppRedirect';
import OAuthSuccess from './pages/oauth/OAuthSuccess';
import HelpCentreLayout from './pages/helpCentre/HelpCentreLayout';

// The help centre pulls in react-markdown/micromark/remark/rehype, ~373 KB
// (~116 KB gzipped) that nothing outside /help-centre links to yet. Loading
// it lazily keeps that weight off every other route, in particular the
// marketing homepage.
const HelpCentreHub = React.lazy(() => import('./pages/helpCentre/HelpCentreHub'));
const HelpArticle = React.lazy(() => import('./pages/helpCentre/HelpArticle'));

// HelpCentreLayout itself stays a static import: it only wraps the already
// site-wide Heading/Footer, so it costs nothing extra on other pages, and
// reusing it here keeps the header/footer chrome stable (no flash-in) while
// the lazy chunk above is still downloading.
const HelpCentreFallback = (): React.JSX.Element => (
  <HelpCentreLayout>
    <div className="hc-container" aria-busy="true" />
  </HelpCentreLayout>
);

export default function App(): React.JSX.Element {
  useScrollToHash();

  useEffect(() => {
    // Run the WAF silent challenge in the background so a token is ready
    // before the user first submits a form.
    void getWafToken();
  }, []);

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/oauth" element={<OAuthSuccess />} />
        <Route path="/app/get" element={<AppGet />} />
        <Route path="/app/callback" element={<AppCallback />} />
        <Route path="/app/*" element={<AppRedirect />} />
        {/* The learn-more section was retired in favour of the help centre +
          OQ assistant; the redirect keeps old bookmarks and indexed links
          landing somewhere useful. */}
        <Route path="/learn-more/*" element={<Navigate to="/help-centre" replace />} />
        <Route path="/help-centre">
          <Route
            index
            element={
              <Suspense fallback={<HelpCentreFallback />}>
                <HelpCentreLayout>
                  <HelpCentreHub />
                </HelpCentreLayout>
              </Suspense>
            }
          />
          <Route
            path=":slug"
            element={
              <Suspense fallback={<HelpCentreFallback />}>
                <HelpArticle />
              </Suspense>
            }
          />
        </Route>
      </Routes>
      <CookieBanner />
    </>
  );
}
